import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Header } from '../../components/layout/Header';
import { useAuth } from '../../lib/auth/AuthContext';
import { switchAccountPlan, AccountPlanViolationError } from '../../lib/api/accounts';
import { Button, PageHeader, StatusBadge } from '../../components/ui';
import type { ProjectPlan } from '../../../../shared/types/plans';
import {
  PLANS,
  SELLABLE_PLANS,
  planRank,
  limitLabel,
  getPlanDef,
} from '../../../../shared/types/plans';
import type { PlanViolation } from '../../../../shared/types/projects';

/**
 * /account/plan — billing plan management for the current account.
 *
 * Shows the account's current plan with its features and limits, and lets
 * owners switch to a different plan. Downgrade switches are validated
 * server-side: the backend checks each project's repo + org count against
 * the new plan's per-project limits and returns a violation list when any
 * project would exceed them.
 */
export const AccountPlanPage: React.FC = () => {
  const { currentAccount } = useAuth();
  const isOwner = currentAccount?.role === 'owner';
  const currentPlan = (currentAccount?.plan ?? 'starter') as ProjectPlan;

  return (
    <div data-testid="account-plan-page" className="min-h-screen bg-slate-50">
      <Header title="Plan" />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <PageHeader
          eyebrow={currentAccount && <span className="font-mono">{currentAccount.id}</span>}
          title={
            <>
              {currentAccount?.name ?? 'Account'}{' '}
              <span className="text-slate-400 font-normal">· plan</span>
            </>
          }
          description="Your account plan controls which features and limits apply to all projects in this workspace."
        />

        {!currentAccount ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : (
          <>
            <CurrentPlanCard
              plan={currentPlan}
              planStatus={currentAccount.planStatus}
            />
            {isOwner ? (
              <PlanSwitcher
                accountId={currentAccount.id}
                currentPlan={currentPlan}
              />
            ) : (
              <div className="text-sm text-slate-500 bg-white border border-slate-200 rounded-xl px-5 py-4">
                Only account owners can change the plan. Contact your account owner to request a plan change.
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Current plan card
// ---------------------------------------------------------------------------

const PLAN_COLORS: Record<ProjectPlan, { badge: string; ring: string }> = {
  starter:    { badge: 'bg-slate-100 text-slate-700 border-slate-300',    ring: 'border-slate-200' },
  team:       { badge: 'bg-blue-50 text-blue-700 border-blue-200',        ring: 'border-blue-200' },
  business:   { badge: 'bg-violet-50 text-violet-700 border-violet-200',  ring: 'border-violet-200' },
  enterprise: { badge: 'bg-amber-50 text-amber-700 border-amber-200',     ring: 'border-amber-200' },
};

const CurrentPlanCard: React.FC<{
  plan: ProjectPlan;
  planStatus: 'active' | 'suspended' | null | undefined;
}> = ({ plan, planStatus }) => {
  const def = getPlanDef(plan);
  const colors = PLAN_COLORS[plan];

  return (
    <div className={`bg-white border-2 ${colors.ring} rounded-xl shadow-card overflow-hidden`}>
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold border ${colors.badge}`}>
          {def.label}
        </span>
        <span className="text-slate-600 text-sm">{def.priceLabel}</span>
        <span className="text-xs text-slate-400">{def.cadence === 'annual' ? 'billed annually' : 'monthly or annual'}</span>
        {planStatus === 'suspended' && (
          <StatusBadge tone="danger" dot>suspended</StatusBadge>
        )}
        {(!planStatus || planStatus === 'active') && (
          <StatusBadge tone="success" dot>active</StatusBadge>
        )}
      </div>

      <div className="px-5 py-4 grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm border-b border-slate-100">
        <LimitCell label="Projects" value={limitLabel(def.limits.maxProjects)} />
        <LimitCell label="Connected repos (per project)" value={limitLabel(def.limits.maxConnectedRepos)} />
        <LimitCell label="Concurrent stories" value={limitLabel(def.limits.maxConcurrentStoriesPerRepo)} />
        <LimitCell label="Audit retention" value={
          def.limits.auditRetentionDays === -1 ? 'Unlimited' : `${def.limits.auditRetentionDays} days`
        } />
        <LimitCell label="Concurrent epics" value={limitLabel(def.limits.concurrentEpicsPerRepo)} />
        <LimitCell label="Agent iterations" value={limitLabel(def.limits.maxIterations)} />
        <LimitCell label="LLM providers" value={def.providers.join(', ')} />
        <LimitCell label="Support" value={def.support} />
      </div>

      <div className="px-5 py-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Features</p>
        <div className="flex flex-wrap gap-1.5">
          {FEATURE_LABELS.map(({ key, label }) => {
            const enabled = !!def.features[key];
            return (
              <span
                key={key}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${
                  enabled
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-slate-50 text-slate-400 border-slate-200 line-through'
                }`}
              >
                {enabled ? '✓' : '✕'} {label}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const LimitCell: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <div className="text-2xs uppercase tracking-wide text-slate-400 font-semibold mb-0.5">{label}</div>
    <div className="text-slate-700 font-medium">{value}</div>
  </div>
);

// ---------------------------------------------------------------------------
// Plan switcher
// ---------------------------------------------------------------------------

const PlanSwitcher: React.FC<{
  accountId: string;
  currentPlan: ProjectPlan;
}> = ({ accountId, currentPlan }) => {
  const queryClient = useQueryClient();
  const { refreshMe } = useAuth();
  const [selected, setSelected] = useState<ProjectPlan | null>(null);
  const [violations, setViolations] = useState<PlanViolation[] | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  const isDowngrade = selected !== null && planRank(selected) < planRank(currentPlan);

  // FORGE-262: starter is not sold — the switcher only offers sellable plans as
  // switch targets. If the account IS currently on starter (a fallback SKU), keep
  // rendering the starter chip (as "current") so the upgrade context is clear, but
  // never as a selectable upgrade target.
  const currentIsStarter = currentPlan === 'starter';
  const switchTargets: ProjectPlan[] = currentIsStarter
    ? ['starter', ...SELLABLE_PLANS]
    : SELLABLE_PLANS;

  const mutation = useMutation({
    mutationFn: (newPlan: ProjectPlan) => switchAccountPlan(accountId, { newPlan }),
    onSuccess: () => {
      // Refresh auth context so currentAccount.plan updates everywhere.
      void refreshMe();
      queryClient.invalidateQueries({ queryKey: ['account'] });
      setSelected(null);
      setViolations(null);
      setConfirmed(false);
    },
    onError: (err) => {
      if (err instanceof AccountPlanViolationError) {
        setViolations(err.violations);
      }
    },
  });

  function handleSelect(plan: ProjectPlan) {
    setSelected(plan);
    setViolations(null);
    setConfirmed(false);
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-card overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100">
        <h2 className="text-sm font-semibold text-slate-800">Change plan</h2>
        <p className="text-xs text-slate-500 mt-0.5">
          Upgrades take effect immediately. For Enterprise, contact your account manager.
        </p>
      </div>

      {/* FORGE-262: existing starter accounts get an upgrade nudge — starter is a
          legacy/fallback SKU that is no longer sold. */}
      {currentIsStarter && (
        <div className="px-5 pt-4">
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
            You&apos;re on the <span className="font-semibold">Starter</span> plan, which is no longer
            sold. Upgrade to <span className="font-semibold">Team</span> or higher to unlock more repos,
            included epic units, and additional features.
          </p>
        </div>
      )}

      {/* Plan option grid */}
      <div className="px-5 py-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {switchTargets.map((plan) => {
          const d = PLANS[plan];
          const isCurrent = plan === currentPlan;
          const isSelected = plan === selected;

          return (
            <button
              key={plan}
              type="button"
              disabled={isCurrent || mutation.isPending}
              onClick={() => handleSelect(plan)}
              className={[
                'relative text-left rounded-lg border px-3 py-3 text-xs transition-colors duration-150 disabled:cursor-not-allowed',
                isCurrent
                  ? 'border-slate-200 bg-slate-50 opacity-60'
                  : isSelected
                  ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-400'
                  : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50',
              ].join(' ')}
            >
              {isCurrent && (
                <span className="absolute top-1.5 right-1.5 text-2xs bg-slate-200 text-slate-600 rounded px-1 py-0.5 font-medium leading-none">
                  current
                </span>
              )}
              <div className="font-semibold text-slate-800 mb-0.5 pr-8">{d.label}</div>
              <div className="text-slate-500">{d.priceLabel}</div>
              <div className="mt-1.5 text-slate-400">
                {limitLabel(d.limits.maxProjects)} project{d.limits.maxProjects !== 1 ? 's' : ''} ·{' '}
                {limitLabel(d.limits.maxConnectedRepos)} repo{d.limits.maxConnectedRepos !== 1 ? 's' : ''}/project
              </div>
              {planRank(plan) < planRank(currentPlan) && !isCurrent && (
                <div className="mt-1 text-2xs text-amber-600 font-medium">↓ downgrade</div>
              )}
            </button>
          );
        })}
      </div>

      {/* Feature delta */}
      {selected && selected !== currentPlan && (
        <div className="px-5 pb-4">
          <PlanDelta currentPlan={currentPlan} newPlan={selected} />
        </div>
      )}

      {/* Violation block */}
      {violations && violations.length > 0 && (
        <div className="px-5 pb-4">
          <div role="alert" className="rounded-lg bg-danger-50 border border-danger-200 px-4 py-3 space-y-2">
            <p className="text-sm font-semibold text-danger-800">
              Cannot switch to {getPlanDef(selected!).label} — usage exceeds new limits
            </p>
            <p className="text-xs text-danger-700">
              Reduce usage in the projects below before switching.
            </p>
            <ViolationList violations={violations} />
          </div>
        </div>
      )}

      {/* Downgrade confirmation */}
      {isDowngrade && selected && !violations && (
        <div className="px-5 pb-4">
          <label className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-0.5 shrink-0"
            />
            <span>
              I understand this is a downgrade from{' '}
              <span className="font-semibold">{getPlanDef(currentPlan).label}</span> to{' '}
              <span className="font-semibold">{getPlanDef(selected).label}</span> and that
              features and limits will be reduced.
            </span>
          </label>
        </div>
      )}

      {/* Non-violation error */}
      {mutation.isError && !(mutation.error instanceof AccountPlanViolationError) && (
        <div className="px-5 pb-4">
          <p role="alert" className="text-xs text-danger-700">
            {(mutation.error as Error).message}
          </p>
        </div>
      )}

      {/* Action row */}
      {selected && selected !== currentPlan && (
        <div className="px-5 py-4 bg-slate-50 border-t border-slate-100 flex items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            loading={mutation.isPending}
            disabled={mutation.isPending || (isDowngrade && !confirmed)}
            onClick={() => mutation.mutate(selected)}
          >
            {mutation.isPending ? 'Switching…' : `Switch to ${getPlanDef(selected).label}`}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={mutation.isPending}
            onClick={() => { setSelected(null); setViolations(null); setConfirmed(false); }}
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Violation list — groups by project
// ---------------------------------------------------------------------------
const ViolationList: React.FC<{ violations: PlanViolation[] }> = ({ violations }) => {
  // Group violations by projectId so each project appears as one block.
  const byProject = new Map<string, { name: string; items: PlanViolation[] }>();
  const accountLevel: PlanViolation[] = [];

  for (const v of violations) {
    if (v.projectId) {
      const key = v.projectId;
      if (!byProject.has(key)) byProject.set(key, { name: v.projectName ?? v.projectId, items: [] });
      byProject.get(key)!.items.push(v);
    } else {
      accountLevel.push(v);
    }
  }

  return (
    <div className="space-y-2 mt-2">
      {accountLevel.map((v) => (
        <ViolationRow key={v.field} violation={v} />
      ))}
      {[...byProject.entries()].map(([id, { name, items }]) => (
        <div key={id} className="space-y-1">
          <p className="text-xs font-semibold text-danger-700">
            Project: <span className="font-mono">{name}</span>
          </p>
          {items.map((v) => <ViolationRow key={`${id}-${v.field}`} violation={v} />)}
        </div>
      ))}
    </div>
  );
};

const ViolationRow: React.FC<{ violation: PlanViolation }> = ({ violation: v }) => (
  <div className="text-xs text-danger-700 flex items-start gap-1.5 pl-2">
    <span className="mt-0.5 text-danger-500">✕</span>
    <span>
      <span className="font-medium">{v.label}:</span> currently{' '}
      <span className="font-mono font-semibold">{v.currentValue}</span>, new limit is{' '}
      <span className="font-mono font-semibold">{v.newLimit}</span>
    </span>
  </div>
);

// ---------------------------------------------------------------------------
// Feature delta between two plans
// ---------------------------------------------------------------------------
const FEATURE_LABELS: Array<{ key: keyof ReturnType<typeof getPlanDef>['features']; label: string }> = [
  { key: 'feature_repo_skills',        label: 'Repo skills' },
  { key: 'feature_mcp_servers',        label: 'MCP servers' },
  { key: 'feature_test_deploy',        label: 'Test deploy' },
  { key: 'feature_smoke_tests',        label: 'Smoke tests' },
  { key: 'feature_post_deploy_validation', label: 'Post-deploy checks' },
  { key: 'feature_custom_agents',      label: 'Custom agents' },
  { key: 'feature_sso_scim',           label: 'SSO / SCIM' },
  { key: 'feature_audit_export_siem',  label: 'Audit export' },
  { key: 'feature_agent_log_access',   label: 'Agent log access' },
];

const PlanDelta: React.FC<{ currentPlan: ProjectPlan; newPlan: ProjectPlan }> = ({
  currentPlan,
  newPlan,
}) => {
  const from = getPlanDef(currentPlan).features;
  const to = getPlanDef(newPlan).features;
  const isUpgrade = planRank(newPlan) > planRank(currentPlan);

  const gained = FEATURE_LABELS.filter((f) => !from[f.key] && to[f.key]);
  const lost   = FEATURE_LABELS.filter((f) => from[f.key] && !to[f.key]);

  if (gained.length === 0 && lost.length === 0) return null;

  const items = isUpgrade ? gained : lost;
  const tone = isUpgrade
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : 'bg-danger-50 text-danger-700 border-danger-200';

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 space-y-2 text-xs">
      <p className="font-semibold text-slate-700">
        {isUpgrade ? "Features you'll gain" : "Features you'll lose"}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {items.map((f) => (
          <span key={f.key} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-medium ${tone}`}>
            {isUpgrade ? '＋' : '－'} {f.label}
          </span>
        ))}
      </div>
    </div>
  );
};

export default AccountPlanPage;
